import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface FavoriteGame {
  id: string;
  fixture_id: number;
  user_id: string;
  created_at: string;
}

export function useFavorites(userId: string | undefined) {
  const [favorites, setFavorites] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Load favorites from cache immediately
  useEffect(() => {
    if (!userId) {
      setFavorites([]);
      setLoading(false);
      return;
    }

    // Load from localStorage cache
    const cacheKey = `favorites_${userId}`;
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        setFavorites(parsed);
      } catch (e) {
        console.error('Error parsing cached favorites:', e);
      }
    }

    fetchFavorites();
  }, [userId]);

  // Real-time updates now handled by unified channel in AppLayout (useRealtimeSync)

  const fetchFavorites = async () => {
    if (!userId) {
      setFavorites([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('favorite_games')
        .select('fixture_id')
        .eq('user_id', userId);

      if (error) throw error;

      const fixtureIds = (data || []).map(fav => fav.fixture_id);
      setFavorites(fixtureIds);

      // Update cache
      const cacheKey = `favorites_${userId}`;
      localStorage.setItem(cacheKey, JSON.stringify(fixtureIds));
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const isFavorite = (fixtureId: number): boolean => {
    return favorites.includes(fixtureId);
  };

  const toggleFavorite = async (fixtureId: number): Promise<boolean> => {
    if (!userId) {
      navigate('/auth');
      return false;
    }

    const isCurrentlyFavorite = isFavorite(fixtureId);

    try {
      if (isCurrentlyFavorite) {
        // Remove from favorites
        const { error } = await supabase
          .from('favorite_games')
          .delete()
          .eq('user_id', userId)
          .eq('fixture_id', fixtureId);

        if (error) throw error;

        // Optimistic update
        setFavorites(prev => prev.filter(id => id !== fixtureId));

        return true;
      } else {
        // Add to favorites
        const { error } = await supabase
          .from('favorite_games')
          .insert({
            user_id: userId,
            fixture_id: fixtureId
          });

        if (error) throw error;

        // Optimistic update
        setFavorites(prev => [...prev, fixtureId]);

        return true;
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast({
        title: "Error",
        description: "Failed to update favorites",
        variant: "destructive"
      });
      return false;
    }
  };

  return {
    favorites,
    loading,
    isFavorite,
    toggleFavorite
  };
}
